import { FC, useEffect, useRef, useState } from "react";
import mermaid from "mermaid";
import { Popover, ConfigProvider } from "antd";
import { toPng } from "html-to-image";

interface MermaidProps {
  code: string;
}

const isDark = () => {
  const theme = document.documentElement.getAttribute("class");
  return Boolean(theme && theme.includes("dark"));
};

function decodeFromUnicode(str:string) {
  return str.replace(/u[0-9a-fA-F]{4}/g, match => {
      return String.fromCharCode(parseInt(match.slice(1), 16));
  });
}

export const Mermaid: FC<MermaidProps> = ({ code }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [touchStartDistance, setTouchStartDistance] = useState(0);
  const [lastTouchScale, setLastTouchScale] = useState(1);
  const [isDarkMode, setIsDarkMode] = useState(isDark());

  const mermaidContainer = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const containerIdRef = useRef(`mermaid-container-${Math.random().toString(36).substr(2, 9)}`);
  const containerId = containerIdRef.current;

  const initMermaid = async (codeToRender?: string) => {
    try {
      const newContainerId = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
      const dark = isDark();
      setIsDarkMode(dark);

      mermaid.initialize({
        theme: dark ? "dark" : "base",
        htmlLabels: true,
        startOnLoad: false,
        sequence: {
          useMaxWidth: true,
        },
        flowchart: {
          useMaxWidth: true,
        },
        themeVariables: {
          edgeLabelBackground: dark ? "#1C1F24" : "#ffffff",
          nodeBorder: dark ? "#575E69" : "transparent",
        },
      });

      if (mermaidContainer.current) {
        const { svg } = await mermaid.render(newContainerId, codeToRender || decodeFromUnicode(code));
        mermaidContainer.current.innerHTML = svg;
      }
    } catch (error) {
      if (mermaidContainer.current) {
        mermaidContainer.current.innerHTML = `
          <div class="p-4 text-red-500 dark:text-red-400 border border-red-300 dark:border-red-600 rounded">
            <div class="font-bold mb-2">Mermaid Syntax Error</div>
            <pre class="text-sm whitespace-pre-wrap">${(error as Error).message}</pre>
          </div>
        `;
      }
    }
  };

  const zoomIn = () => setScale((s) => Math.min(s + 0.1, 3));
  const zoomOut = () => setScale((s) => Math.max(s - 0.1, 0.5));
  const resetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const distance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
      setTouchStartDistance(distance);
      setLastTouchScale(scale);
    } else if (e.touches.length === 1) {
      e.preventDefault();
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({
        x: touch.clientX - position.x,
        y: touch.clientY - position.y,
      });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const currentDistance = Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);

      const scaleFactor = currentDistance / touchStartDistance;
      const newScale = Math.min(Math.max(lastTouchScale * scaleFactor, 0.5), 3);

      const centerX = (touch1.clientX + touch2.clientX) / 2;
      const centerY = (touch1.clientY + touch2.clientY) / 2;

      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const relativeX = centerX - rect.left;
        const relativeY = centerY - rect.top;

        const scaleChange = newScale - scale;
        setPosition({
          x: position.x - ((relativeX - rect.width / 2) * scaleChange) / newScale,
          y: position.y - ((relativeY - rect.height / 2) * scaleChange) / newScale,
        });
        setScale(newScale);
      }
    } else if (e.touches.length === 1 && isDragging) {
      e.preventDefault();
      const touch = e.touches[0];
      setPosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y,
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleWheel = (e: WheelEvent) => {
    if (!containerRef.current) return;

    if (e.ctrlKey || e.metaKey || e.deltaY % 1 !== 0) {
      e.preventDefault();
      const delta = -e.deltaY * 0.01;
      const newScale = Math.min(Math.max(scale + delta, 0.5), 3);

      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const scaleChange = newScale - scale;
      setPosition({
        x: position.x - ((mouseX - rect.width / 2) * scaleChange) / newScale,
        y: position.y - ((mouseY - rect.height / 2) * scaleChange) / newScale,
      });
      setScale(newScale);
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const downloadSVG = () => {
    const svg = mermaidContainer.current?.querySelector("svg");
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([svgData], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `Juchats-Mermaid-${Date.now()}.svg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadPNG = async () => {
    try {
      // 1. 获取当前渲染的 SVG 元素
      const currentSvg = mermaidContainer.current?.querySelector("svg");
      if (!currentSvg) return;

      // 2. 创建 SVG 副本
      const svgClone = currentSvg.cloneNode(true) as HTMLElement;

      // 3. 设置基本属性
      svgClone.setAttribute("preserveAspectRatio", "xMidYMid meet");

      // 4. 创建临时容器
      const tempContainer = document.createElement("div");
      tempContainer.appendChild(svgClone);
      document.body.appendChild(tempContainer);

      try {
        // 5. 转换为 PNG
        const png = await toPng(svgClone, {
          quality: 1,
          pixelRatio: 2,
          skipFonts: true,
          filter: (node) => {
            return node.tagName === "svg" || node.parentElement?.tagName === "svg";
          },
        });

        // 6. 下载文件
        const link = document.createElement("a");
        link.href = png;
        link.download = `Juchats-Mermaid-${Date.now()}.png`;
        link.click();
      } finally {
        // 7. 清理临时元素
        document.body.removeChild(tempContainer);
      }
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const content = (
    <div className="flex flex-col gap-2 font-lexend text-12px ">
      <button
        className="w-full text-left hover:bg-[var(--user-info-bg)] hover:text-black dark:text-white cursor-pointer py-2 flex items-center justify-between rounded-md px-4"
        onClick={downloadPNG}
      >
        PNG
      </button>
      <button
        className="w-full text-left hover:bg-[var(--user-info-bg)] hover:text-black dark:text-white cursor-pointer py-2 flex items-center justify-between rounded-md px-4"
        onClick={downloadSVG}
      >
        SVG
      </button>
    </div>
  );

  useEffect(() => {
    initMermaid();

    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === "attributes" && mutation.attributeName === "class") {
          initMermaid();
        }
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      observer.disconnect();
    };
  }, [code]);

  return (
    <div
      className={`${
        isFullscreen ? "fixed left-0 top-0 size-full inset-0 z-[100] bg-white dark:bg-[#1c1f24]" : "relative"
      }`}
    >
      <div className={`top-0 mb-2 flex gap-2 bg-inherit p-2 ${isFullscreen ? "w-full z-[101]" : "z-[10]"}`}>
        <button
          className="w-[32px] h-[30px] flex justify-center items-center border rounded px-2 py-1 text-sm dark:border-[#575E69] dark:text-[#D8D8D8]"
          onClick={zoomIn}
        >
          <svg
            viewBox="0 0 24 24"
            display="inline-block"
            vertical-align="middle"
            width="1em"
            height="1em"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="currentColor"
              d="m18.031 16.617l4.283 4.282l-1.415 1.415l-4.282-4.283A8.96 8.96 0 0 1 11 20c-4.968 0-9-4.032-9-9s4.032-9 9-9s9 4.032 9 9a8.96 8.96 0 0 1-1.969 5.617m-2.006-.742A6.98 6.98 0 0 0 18 11c0-3.867-3.133-7-7-7s-7 3.133-7 7s3.133 7 7 7a6.98 6.98 0 0 0 4.875-1.975zM10 10V7h2v3h3v2h-3v3h-2v-3H7v-2z"
            />
          </svg>
        </button>
        <button
          className="w-[32px] h-[30px] flex justify-center items-center border rounded px-2 py-1 text-sm dark:border-[#575E69] dark:text-[#D8D8D8]"
          onClick={zoomOut}
        >
          <svg
            viewBox="0 0 24 24"
            display="inline-block"
            vertical-align="middle"
            width="1em"
            height="1em"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="currentColor"
              d="m18.031 16.617l4.283 4.282l-1.415 1.415l-4.282-4.283A8.96 8.96 0 0 1 11 20c-4.968 0-9-4.032-9-9s4.032-9 9-9s9 4.032 9 9a8.96 8.96 0 0 1-1.969 5.617m-2.006-.742A6.98 6.98 0 0 0 18 11c0-3.867-3.133-7-7-7s-7 3.133-7 7s3.133 7 7 7a6.98 6.98 0 0 0 4.875-1.975zM7 10h8v2H7z"
            />
          </svg>
        </button>
        <button
          className="w-[32px] h-[30px] flex justify-center items-center border rounded px-2 py-1 text-sm dark:border-[#575E69] dark:text-[#D8D8D8]"
          onClick={resetZoom}
        >
          <svg
            viewBox="0 0 24 24"
            display="inline-block"
            vertical-align="middle"
            width="1em"
            height="1em"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="currentColor"
              d="M5.463 4.433A9.96 9.96 0 0 1 12 2c5.523 0 10 4.477 10 10c0 2.136-.67 4.116-1.81 5.74L17 12h3A8 8 0 0 0 6.46 6.228zm13.074 15.134A9.96 9.96 0 0 1 12 22C6.477 22 2 17.523 2 12c0-2.136.67-4.116 1.81-5.74L7 12H4a8 8 0 0 0 13.54 5.772z"
            />
          </svg>
        </button>
        <ConfigProvider
          theme={{
            components: {
              Popover: {
                borderRadiusLG: 4,
                boxShadowSecondary: "none",
                colorBgElevated: isDarkMode ? "#1d1e1f" : "#ffffff",
              },
            },
          }}
        >
          <Popover
            content={content}
            trigger="click"
            placement="bottomLeft"
            align={{ offset: [0, 10] }}
            arrow={false}
            rootClassName="w-[150px] rounded-[4px] mt-[10px] shadow-none border dark:border-[#575E69]"
          >
            <button className="w-[32px] h-[30px] flex justify-center items-center border rounded px-2 py-1 text-sm dark:border-[#575E69] dark:text-[#D8D8D8]">
              <svg
                viewBox="0 0 24 24"
                display="inline-block"
                vertical-align="middle"
                width="1em"
                height="1em"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fill="currentColor"
                  d="M3 19h18v2H3zm10-5.828L19.071 7.1l1.414 1.414L12 17L3.515 8.515L4.929 7.1L11 13.173V2h2z"
                />
              </svg>
            </button>
          </Popover>
        </ConfigProvider>
        <button
          className="w-[32px] h-[30px] flex justify-center items-center border rounded px-2 py-1 text-sm dark:border-[#575E69] dark:text-[#D8D8D8]"
          onClick={() => {
            setIsFullscreen(!isFullscreen);
            resetZoom();
          }}
        >
          {isFullscreen ? (
            <svg
              viewBox="0 0 24 24"
              display="inline-block"
              vertical-align="middle"
              width="1em"
              height="1em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path fill="currentColor" d="M18 7h4v2h-6V3h2zM8 9H2V7h4V3h2zm10 8v4h-2v-6h6v2zM8 15v6H6v-4H2v-2z" />
            </svg>
          ) : (
            <svg
              viewBox="0 0 24 24"
              display="inline-block"
              vertical-align="middle"
              width="1em"
              height="1em"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path fill="currentColor" d="M8 3v2H4v4H2V3zM2 21v-6h2v4h4v2zm20 0h-6v-2h4v-4h2zm0-12h-2V5h-4V3h6z" />
            </svg>
          )}
        </button>
      </div>
      <div
        ref={containerRef}
        className={`mermaid-container relative overflow-hidden ${
          isFullscreen ? "h-[calc(100vh-48px)] lt-md:mt-12" : "h-[200px] md:h-[400px]"
        }`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        onWheel={handleWheel as any}
      >
        <div
          id={containerId}
          ref={mermaidContainer}
          className="mermaid absolute h-full w-full flex items-center justify-center"
          style={{
            transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
          }}
        />
      </div>
    </div>
  );
};
